import { useMemo, useState } from 'react';
import { Badge, Button, Panel, ScreenHeader, StateBox, Table, humanize, type Column } from '@garnet/ui';
import { useAsync } from '../useAsync.js';
import type { AdminApi, AdminSource } from '../data/types.js';
import { canPromote, circuitTone, isStale, quotaPct, sourceHealth, statusTone } from '../data/shape.js';
import { StatStrip, NameCell, TagRow, ageLabel } from '../components/AdminBits.js';

/**
 * Source registry + health (PRD §20). Per-source reachability, freshness against the window,
 * quota, and circuit-breaker state per source and region; a connected `planned` source can
 * be promoted to `live` (the promote gate is `shape.canPromote`). Promotion is local to this
 * offline console session.
 */
export function Sources({ api }: { api: AdminApi }) {
  const { data, loading, error } = useAsync(() => api.getSources());
  const [promoted, setPromoted] = useState<ReadonlySet<string>>(new Set());

  const rows = useMemo<AdminSource[]>(
    () => (data ?? []).map((s) => (promoted.has(s.id) ? { ...s, status: 'live', connection: 'connected' } : s)),
    [data, promoted],
  );

  if (loading) return <StateBox kind="loading" title="Loading sources" />;
  if (error || !data) return <StateBox kind="error" title="Could not load the source registry">{error}</StateBox>;

  const counts = { live: 0, degraded: 0, down: 0, planned: 0 } as Record<AdminSource['status'], number>;
  for (const s of rows) counts[s.status] += 1;

  const columns: Column<AdminSource>[] = [
    {
      key: 'name',
      header: 'Source',
      render: (s) => <NameCell name={s.name} sub={`${humanize(s.protocol)} · ${humanize(s.auth)} · ${s.mapping_version}`} />,
    },
    { key: 'caps', header: 'Capabilities', render: (s) => <TagRow tags={s.capabilities} /> },
    { key: 'region', header: 'Residency', mono: true, render: (s) => (s.residency ?? '—').toUpperCase() },
    { key: 'status', header: 'Status', render: (s) => <Badge tone={statusTone(s.status)}>{s.status}</Badge> },
    { key: 'health', header: 'Health', render: (s) => {
      const h = sourceHealth(s);
      return <Badge tone={h.tone}>{h.label}</Badge>;
    } },
    {
      key: 'fresh',
      header: 'Freshness',
      mono: true,
      render: (s) => {
        const stale = isStale(s.health.last_success_age_seconds, s.freshness_window_seconds);
        return (
          <span style={{ color: stale ? 'var(--status-fail-text)' : 'var(--fg-2)' }}>
            {ageLabel(s.health.last_success_age_seconds)} / {ageLabel(s.freshness_window_seconds)}
            {stale ? ' · stale' : ''}
          </span>
        );
      },
    },
    {
      key: 'quota',
      header: 'Quota',
      mono: true,
      render: (s) =>
        s.health.quota_limit_per_day === null
          ? '∞'
          : `${quotaPct(s.health.quota_used_today, s.health.quota_limit_per_day)}%`,
    },
    { key: 'circuit', header: 'Breaker', render: (s) => <Badge tone={circuitTone(s.health.circuit)}>{s.health.circuit}</Badge> },
    {
      key: 'action',
      header: '',
      render: (s) =>
        promoted.has(s.id) ? (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--status-pass-text)' }}>promoted</span>
        ) : canPromote(s) ? (
          <Button sm variant="primary" icon="arrow" onClick={() => setPromoted((p) => new Set(p).add(s.id))}>
            Promote
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="screen">
      <ScreenHeader eyebrow="Connector fabric" title="Source registry & health" lead="Per-source reachability, freshness against the window, quota and circuit-breaker state — by source and by region." />
      <StatStrip
        stats={[
          { label: 'Sources', value: rows.length },
          { label: 'Live', value: counts.live, tone: 'pass' },
          { label: 'Degraded', value: counts.degraded, tone: 'warn' },
          { label: 'Down', value: counts.down, tone: 'fail' },
          { label: 'Planned', value: counts.planned },
        ]}
      />
      <Panel
        title="Source registry & health"
        right={<span className="metatext">per source · per region</span>}
        flush
        bodyStyle={{ maxHeight: 560, overflow: 'auto' }}
      >
        <Table columns={columns} rows={rows} rowKey={(s) => s.id} caption="Connector reachability, freshness, quota and circuit-breaker state" />
      </Panel>
    </div>
  );
}
